import { apiClient as api } from './api';
import {
  PackType,
  ExportFormat,
  CreateImagePackRequest,
  ImagePackCreationResponse,
  GeneratePromptsRequest,
  UpdateImagePackRequest,
  GenerateImagesRequest,
  CreatePromptPackRequest,
  PromptPackCreationResponse,
  AddPromptsRequest,
  TestPromptRequest,
  CreateSocialPackRequest,
  SocialPackCreationResponse,
  GeneratePostsRequest,
  UpdateSocialPackPostsRequest,
  UpdateMetadataRequest,
  ExportPackRequest,
  ImportPackRequest,
  ImportPackResponse,
  PackStatusResponse,
  ContentPackSession
} from '../models/contentPack';

const CONTENT_PACK_STORAGE_KEY = 'content_pack_sessions';

// Helper functions for session management
const saveSession = (session: ContentPackSession): void => {
  const sessions = JSON.parse(localStorage.getItem(CONTENT_PACK_STORAGE_KEY) || '{}');
  sessions[session.sessionId] = session;
  localStorage.setItem(CONTENT_PACK_STORAGE_KEY, JSON.stringify(sessions));
};

const getSession = (sessionId: string): ContentPackSession | null => {
  const sessions = JSON.parse(localStorage.getItem(CONTENT_PACK_STORAGE_KEY) || '{}');
  return sessions[sessionId] || null;
};

const getAllSessions = (): Record<string, ContentPackSession> => {
  return JSON.parse(localStorage.getItem(CONTENT_PACK_STORAGE_KEY) || '{}');
};

const deleteSession = (sessionId: string): void => {
  const sessions = JSON.parse(localStorage.getItem(CONTENT_PACK_STORAGE_KEY) || '{}');
  delete sessions[sessionId];
  localStorage.setItem(CONTENT_PACK_STORAGE_KEY, JSON.stringify(sessions));
};

export const contentPackService = {
  // Image Pack endpoints
  async createImagePack(request: CreateImagePackRequest): Promise<ImagePackCreationResponse> {
    const response = await api.post('/api/content-packs/image-pack/create', request);
    const session: ContentPackSession = {
      sessionId: response.data.sessionId,
      packType: PackType.Image,
      packDetails: response.data.packDetails,
      status: 'created',
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveSession(session);
    return response.data;
  },

  async generatePrompts(sessionId: string, request: GeneratePromptsRequest): Promise<any> {
    const response = await api.post(`/api/content-packs/image-pack/${sessionId}/generate-prompts`, request);
    const session = getSession(sessionId);
    if (session && session.packType === PackType.Image) {
      // Handle actual API response format where prompts are at root level
      const promptsFromResponse = response.data.prompts || response.data.packDetails?.prompts || [];
      
      session.packDetails = {
        ...session.packDetails,
        ...response.data.packDetails,
        // Convert string prompts to ImagePrompt objects
        prompts: promptsFromResponse.map((prompt: string | any, index: number) => ({
          id: `prompt-${index}`,
          prompt: typeof prompt === 'string' ? prompt : prompt.prompt,
          variations: [],
          metadata: typeof prompt === 'object' ? prompt : {}
        })),
        // Update the count based on actual prompts generated
        imageCount: promptsFromResponse.length
      };
      session.status = response.data.status || session.status;
      session.progress = response.data.status === 'prompts_generated' ? 100 : session.progress;
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  async updateImagePack(sessionId: string, request: UpdateImagePackRequest): Promise<any> {
    const response = await api.put(`/api/content-packs/image-pack/${sessionId}/update`, request);
    const session = getSession(sessionId);
    if (session && session.packType === PackType.Image) {
      session.packDetails = { ...session.packDetails, ...response.data.packDetails };
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  async generateImages(sessionId: string, request: GenerateImagesRequest): Promise<any> {
    const response = await api.post(`/api/content-packs/image-pack/${sessionId}/generate-images`, request);
    const session = getSession(sessionId);
    if (session) {
      session.status = 'generating';
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  // Prompt Pack endpoints
  async createPromptPack(request: CreatePromptPackRequest): Promise<PromptPackCreationResponse> {
    const response = await api.post('/api/content-packs/prompt-pack/create', request);
    const session: ContentPackSession = {
      sessionId: response.data.sessionId,
      packType: PackType.Prompt,
      packDetails: response.data.packDetails,
      status: 'created',
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveSession(session);
    return response.data;
  },

  async addPrompts(sessionId: string, request: AddPromptsRequest): Promise<any> {
    const response = await api.post(`/api/content-packs/prompt-pack/${sessionId}/add-prompts`, request);
    const session = getSession(sessionId);
    if (session && session.packType === PackType.Prompt) {
      session.packDetails = response.data.packDetails;
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  async testPrompt(sessionId: string, request: TestPromptRequest): Promise<any> {
    const response = await api.post(`/api/content-packs/prompt-pack/${sessionId}/test-prompt`, request);
    return response.data;
  },

  // Social Pack endpoints
  async createSocialPack(request: CreateSocialPackRequest): Promise<SocialPackCreationResponse> {
    const response = await api.post('/api/content-packs/social-pack/create', request);
    const session: ContentPackSession = {
      sessionId: response.data.sessionId,
      packType: PackType.Social,
      packDetails: response.data.packDetails,
      status: 'created',
      progress: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    saveSession(session);
    return response.data;
  },

  async generatePosts(sessionId: string, request: GeneratePostsRequest): Promise<any> {
    const response = await api.post(`/api/content-packs/social-pack/${sessionId}/generate-posts`, request);
    const session = getSession(sessionId);
    if (session && session.packType === PackType.Social) {
      session.packDetails = response.data.packDetails;
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  async updateSocialPosts(sessionId: string, request: UpdateSocialPackPostsRequest): Promise<any> {
    const response = await api.put(`/api/content-packs/social-pack/${sessionId}/update-posts`, request);
    const session = getSession(sessionId);
    if (session && session.packType === PackType.Social) {
      session.packDetails = response.data.packDetails;
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  async updateMetadata(sessionId: string, request: UpdateMetadataRequest): Promise<any> {
    const response = await api.patch(`/api/content-packs/social-pack/${sessionId}/metadata`, request);
    const session = getSession(sessionId);
    if (session) {
      session.packDetails = { ...session.packDetails, metadata: request.metadata };
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  // Common endpoints
  async exportPack(sessionId: string, request: ExportPackRequest): Promise<any> {
    const response = await api.post(`/api/content-packs/${sessionId}/export`, request);
    return response.data;
  },

  async importPack(request: ImportPackRequest): Promise<ImportPackResponse> {
    const response = await api.post('/api/content-packs/import', request);
    return response.data;
  },

  async getPackStatus(sessionId: string): Promise<PackStatusResponse> {
    const response = await api.get(`/api/content-packs/${sessionId}/status`);
    const session = getSession(sessionId);
    if (session && response.data.progress !== undefined) {
      session.status = response.data.status;
      session.progress = response.data.progress;
      session.updatedAt = new Date().toISOString();
      saveSession(session);
    }
    return response.data;
  },

  async downloadPack(sessionId: string): Promise<Blob> {
    const response = await api.get(`/api/content-packs/${sessionId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  async exportMarkdown(sessionId: string): Promise<string> {
    const response = await api.get(`/api/content-packs/${sessionId}/export/markdown`);
    return response.data;
  },

  async exportText(sessionId: string): Promise<string> {
    const response = await api.get(`/api/content-packs/${sessionId}/export/text`);
    return response.data;
  },

  // Local storage management
  getSession,
  getAllSessions,
  saveSession,
  deleteSession,
  getAllSessionIds: () => Object.keys(getAllSessions()),
  getSessionsByType: (packType: PackType) => {
    const sessions = getAllSessions();
    return Object.entries(sessions)
      .filter(([_, session]) => session.packType === packType)
      .map(([id, session]) => session);
  }
};